import * as Yup from 'yup';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormHelperText, Grid } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReceiptAlert, ReceiptAlertType, getReceiptAlertTypeTranslationId } from '@synergy/models';
import { TrashIcon } from '@synergy/theme';
import { createRef, useEffect } from 'react';

import { PoolButton } from '../button/pool-button';
import { PoolModal } from '../modal/pool-modal';
import { PoolSelect } from '../select';
import { PoolTextArea } from '../textarea/pool-textarea';

interface PoolSubToolbarAlertModalProps {
    alert?: ReceiptAlert;
    isVisible?: boolean;
    onClose?: () => void;
    onAlertChange?: (alert: ReceiptAlert) => void;
    onAlertDelete?: () => void;
}

const validationSchema = Yup.object().shape({
    type: Yup.string().required('app.error.required'),
    text: Yup.string().required('app.error.requiredText'),
});

const options = Object.values(ReceiptAlertType).map(key => ({
    value: key,
    displayValue: <FormattedMessage id={getReceiptAlertTypeTranslationId(key)} />,
}));

export const PoolSubToolbarAlertModal = (props: PoolSubToolbarAlertModalProps) => {
    const ref = createRef<FormikProps<ReceiptAlert>>();

    useEffect(() => {
        if (props.isVisible) {
            ref.current?.resetForm();
        }
    }, [props.isVisible, ref]);

    const handleSubmit = (value: ReceiptAlert) => {
        props.onAlertChange?.(value);
    };

    return (
        <Formik
            initialValues={props.alert ?? { type: ReceiptAlertType.Information, text: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            innerRef={ref}
        >
            {formikProps => (
                <PoolModal
                    isVisible={!!props.isVisible}
                    title={
                        props.alert ? (
                            <FormattedMessage id="components.receiptAlert.title.edit" />
                        ) : (
                            <FormattedMessage id="components.receiptAlert.title.add" />
                        )
                    }
                    actionChildren={[
                        <PoolButton
                            key="1"
                            variant="text"
                            styleType="firstLevel"
                            color="secondary"
                            onClick={props.onAlertDelete}
                            disabled={!props.alert}
                            startIcon={<TrashIcon />}
                        >
                            <FormattedMessage id="app.common.delete" />
                        </PoolButton>,
                        <PoolButton
                            id="save-free-article-position"
                            key="2"
                            styleType="firstLevel"
                            onClick={formikProps.submitForm}
                        >
                            {props.alert ? (
                                <FormattedMessage id="app.common.save" />
                            ) : (
                                <FormattedMessage id="app.common.add" />
                            )}
                        </PoolButton>,
                    ]}
                    handleClose={() => props.onClose?.()}
                >
                    <Grid container gap={2}>
                        <Grid item xs={12}>
                            <Field name="type">
                                {({ field, form, meta }: FieldProps<number>) => (
                                    <>
                                        <PoolSelect
                                            fullWidth={true}
                                            label={<FormattedMessage id="components.receiptAlert.type" />}
                                            value={field.value}
                                            disallowDefaultValue={true}
                                            options={options}
                                            onValueChange={value => form.setFieldValue(field.name, value)}
                                            required={true}
                                        />
                                        {meta.error && meta.touched && (
                                            <FormHelperText error={true}>
                                                <FormattedMessage id={meta.error} />
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            </Field>
                        </Grid>
                        <Grid item xs={12}>
                            <Field name="text">
                                {({ field, meta }: FieldProps<number>) => (
                                    <PoolTextArea
                                        {...field}
                                        fullWidth={true}
                                        label={<FormattedMessage id="components.receiptAlert.text" />}
                                        rows={5}
                                        required={true}
                                        error={meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                    />
                                )}
                            </Field>
                        </Grid>
                    </Grid>
                </PoolModal>
            )}
        </Formik>
    );
};
